/**
 * DimensionFilter Component
 *
 * Multi-select filter for conduct dimensions.
 * Default is OR logic (match ANY), user can toggle to AND (match ALL).
 */

import { Layers } from 'lucide-react';
import { FilterDropdown } from './FilterDropdown';
import type { DimensionFilterProps, ConductDimension } from '../types/filter.types';
import { DIMENSION_OPTIONS, DEFAULT_FILTER_LOGIC } from '../constants/filter.constants';
import { useFilterWithLogic } from '../hooks/useFilterWithLogic';

export function DimensionFilter({
  value,
  onChange,
  disabled = false,
  className,
  showTcf = false,
}: DimensionFilterProps) {
  // A dimension already selected stays on the menu even when the collection
  // cannot offer it, so the trigger names it and the user can clear it. Dropping
  // the value instead would silently widen the result set: an empty dimensions
  // list means "no dimension filter", not "no matches".
  const options = DIMENSION_OPTIONS.filter(
    (option) => option.value !== 'tcf' || showTcf || !!value?.values?.includes('tcf')
  );

  const { currentLogic, handleValuesChange, handleLogicChange, helperText } =
    useFilterWithLogic<ConductDimension>({
      value,
      onChange,
      defaultLogic: DEFAULT_FILTER_LOGIC.dimensions,
      noun: 'dimensions',
    });

  return (
    <FilterDropdown
      value={value?.values}
      onChange={handleValuesChange}
      options={options}
      icon={<Layers className="h-4 w-4" />}
      emptyLabel="All Dimensions"
      pluralNoun="dimensions"
      helperText={helperText}
      showDescriptions
      disabled={disabled}
      className={className}
      logicMode={currentLogic}
      onLogicModeChange={handleLogicChange}
    />
  );
}
